import { computed } from "vue";
import { storeToRefs } from "pinia";
import type {
	ProductItem,
	SimplifiedProductRelations
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { ProductPrefix } from "~/utils/constants";
import {
	findRelationsByPrefix,
	mergeRelationItems
} from "~/utils/product/relations";

/** Marker attribute `scrollToCombineWith` queries, so the two can't drift. */
export const COMBINE_WITH_ATTR = "data-combine-with";

/** How far down the viewport the block may start and still count as seen. */
const VISIBLE_FRACTION = 0.8;

/**
 * Cross-sell products from the Norce `combine` relation group. Relations are
 * already in the store, so this costs no extra request.
 */
export const useCombineWith = () => {
	const { relations } = storeToRefs(useProductStore());

	/** Every `combine*` group, not the first: the page hides them all from the
	 * fallback carousel, so a second group would otherwise render nowhere. */
	const groups = computed<SimplifiedProductRelations[]>(() =>
		findRelationsByPrefix(
			relations.value as SimplifiedProductRelations[],
			ProductPrefix.COMBINE
		)
	);

	const items = computed<ProductItem[]>(() => mergeRelationItems(groups.value));

	const hasItems = computed(() => items.value.length > 0);

	return { items, hasItems };
};

/**
 * Scrolls down to the block after a successful add. Never up: the block sits
 * above the buy column below `lg`, and Add to Cart is ~1000px under it there —
 * jumping back would drag the shopper past the product they just bought.
 *
 * No-ops when the block isn't rendered, is hidden, or is already in view.
 * Focus follows only a real scroll, so the keyboard lands on what the page
 * just moved to and stays put otherwise.
 */
export const scrollToCombineWith = () => {
	if (typeof document === "undefined") return;

	const block = Array.from(
		document.querySelectorAll<HTMLElement>(`[${COMBINE_WITH_ATTR}]`)
	).find((element) => element.offsetParent !== null);
	if (!block) return;

	// One test covers both directions: a block above the fold has been passed,
	// and one only peeking over the bottom edge has not been read. The top
	// edge, not the whole block — a block taller than the viewport is never
	// fully visible and would jump on every add.
	const { top } = block.getBoundingClientRect();
	if (top < window.innerHeight * VISIBLE_FRACTION) return;

	const prefersReducedMotion =
		window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

	block.scrollIntoView({
		behavior: prefersReducedMotion ? "auto" : "smooth",
		block: "start"
	});

	// Only after a move: the block precedes Add to Cart in the DOM, so taking
	// focus without scrolling would cost a keyboard shopper every card to get
	// back to the button.
	block.focus({ preventScroll: true });
};
